import React, { createContext, useContext, useEffect, useState } from 'react';
import { StatusCode } from '../../components/Alert.tsx';
import CategoriesController from '../../controllers/CategoriesController.ts';
import { Category } from '../../models/category';
import { BaseSearchParam } from '../../models/searchParams/baseSearchParam';
import { TableSearchParam } from '../../models/searchParams/tableSearchParam';
import { useAlert } from '../alertProvider.tsx';

type CategoriesContextType = {
  isLoaded?: boolean;
  categories: Category[];
  totalCount: number;
  tableSearchParam: TableSearchParam;
  setTableSearchParam: (tableSearchParam: TableSearchParam) => void;
  searchParam: BaseSearchParam;
  setSearchParam: (baseSearchParam: BaseSearchParam) => void;
  deleteCategory: (categoryID: number) => {};
};

const CategoriesContext = createContext<CategoriesContextType>({} as CategoriesContextType);

export const CategoriesContextProvider = ({ children }: { children: any }) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 25 });
  const [searchParam, setSearchParam] = useState<BaseSearchParam>({} as BaseSearchParam);
  const { showAlert } = useAlert();

  const fetchCategories = async () => {
    try {
      setIsLoaded(false);
      const result = await CategoriesController.get(tableSearchParam, searchParam);
      setCategories(result.categories);
      setTotalCount(result.totalCount);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoaded(true);
    }
  };
  useEffect(() => {
    fetchCategories();
  }, [tableSearchParam, searchParam]);

  const deleteCategory = async (categoryID: number) => {
    try {
      const result = await CategoriesController.delete(categoryID);
      if (result.notificationStatus == StatusCode.success)
        fetchCategories();
      else
        showAlert(result.notificationStatus, result.notificationMessage);
    } catch (error) {
      console.error('Error deleting category:', error);
    } finally {
      setIsLoaded(true);
    }
  }

  const context: CategoriesContextType = {
    isLoaded,
    categories,
    totalCount,
    tableSearchParam,
    setTableSearchParam,
    searchParam,
    setSearchParam,
    deleteCategory
  }

  return (
    <CategoriesContext.Provider value={context}>
      {children}
    </CategoriesContext.Provider>
  )
}

export const useCategoriesContext = () => useContext(CategoriesContext)
